/**
 * The staff audit trail — every mutation `wend_admin` made, filtered to one
 * tenant.
 *
 * WHY THIS READ EXISTS AT ALL, and why it is not optional decoration on the
 * detail screen. The reason field on both forms is required, and the
 * justification for making it required is a specific question asked six months
 * later: *"why is this tenant's balance 50,000?"* A required reason that nothing
 * can read back does not answer that question — it only makes the person who
 * typed it feel like it did. The write side and the read side are one feature,
 * and shipping the write half alone is how an audit table becomes a table
 * nobody has ever opened.
 *
 * THE TENANT IS IN THE PATH, AND ONLY IN THE PATH. `adminAuditListQuerySchema`
 * in `@wend/shared` offers `tenantId`, `action` and `adminUserId` as optional
 * filters, for a staff-wide view this slice does not build; the API's route is
 * nested under the workspace and narrows that schema by **omitting** `tenantId`
 * from it. So this module sends pagination and nothing else. Sending the id in
 * both places would create a request that can name two different workspaces, and
 * the one that loses is the one nobody was looking at.
 *
 * THE ROWS ARE A DISCRIMINATED UNION on `action`, and the screen must render
 * both members rather than a lowest common denominator. A `tenant.updated` row
 * carries `changes`; a `tenant.credits_granted` row carries an amount, a
 * channel, a ledger entry id and the balance either side. Flattening them into
 * one "what happened" string would throw away the two facts a support
 * conversation actually needs — which ledger entry, and what the balance was
 * before it.
 */
import {
  adminAuditListResponseSchema,
  type AdminAuditListResponse,
} from '@wend/shared';

import { tenantAuditPath } from './admin-endpoints';
import { apiClient } from './api-client';

export interface TenantAuditParams {
  readonly tenantId: string;
  readonly page: number;
  readonly pageSize: number;
}

export async function listTenantAudit(
  params: TenantAuditParams,
  signal?: AbortSignal,
): Promise<AdminAuditListResponse> {
  return apiClient.request({
    path: tenantAuditPath(params.tenantId),
    schema: adminAuditListResponseSchema,
    query: { page: params.page, pageSize: params.pageSize },
    ...(signal === undefined ? {} : { signal }),
  });
}
